import React, { useState, useCallback, useEffect, useRef } from 'react';
import { S, readableOn } from '../styles';
import { getTheme } from '../theme';
import { API, TECH_COLORS, AGE_BUCKETS, fmtTime, authHeaders } from '../utils/helpers';
import TicketDetail from './TicketDetail';
import CustomerPopover from './CustomerPopover';
import ManagerReviewWizard from './ManagerReviewWizard';
import NewTicketModal from './NewTicketModal';
import PaymentBadge from './PaymentBadge';
import CountPill from './CountPill';
import { useTimeTotals, TimeBadge } from './timeTotals';

export default function ServiceQueue({ theme = 'dark', currentUser = null }) {
  const T = getTheme(theme);
  const isDark = theme === 'dark';
  const [tickets, setTickets] = useState([]);
  const [summary, setSummary] = useState(null);
  // Set when this list was built the slow way and is therefore not live. The
  // screen says how old it is and nothing more.
  //
  // It deliberately does NOT explain why. An earlier version described the
  // Lightspeed fault in full, which is the right level of detail for a log and
  // the wrong one for a person at the counter: it reads as the software
  // apologising for itself, and a tech who is told the plumbing is broken stops
  // trusting the screen even when the tickets on it are correct. What they need
  // is the timestamp, so they know whether to refresh before acting.
  const [stale, setStale] = useState(false);
  const [staleAt, setStaleAt] = useState(null);
  // No list yet at all — the first read of the whole table is still running. It
  // takes a few minutes and nothing waits on it, so the screen has to say what
  // is happening rather than show an empty queue as though that were the answer.
  const [building, setBuilding] = useState(false);
  const staleAgeText = () => {
    if (!staleAt) return 'just now';
    const mins = Math.round((Date.now() - staleAt) / 60000);
    if (mins < 1) return 'just now';
    return mins === 1 ? '1 minute ago' : mins + ' minutes ago';
  };
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [loadError, setLoadError] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState('risk');
  const [sortDir, setSortDir] = useState('desc');
  const [hoveredNote, setHoveredNote] = useState(null);
  const [customerPopover, setCustomerPopover] = useState(null);
  const customerAnchorRefs = useRef({});
  const [managerReview, setManagerReview] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [shopProfile, setShopProfile] = useState(null);
  const [statusMeta, setStatusMeta] = useState([]);
  const [viewMode, setViewMode] = useState('lightspeed');
  // Sorting in the Lightspeed view (7 Oct). null = Lightspeed's own order
  // (status order, then oldest in first), which is where it starts and where a
  // third click on the same header returns. Remembered in this browser only.
  const LS_SORT_KEY = 'spinboard-queue-ls-sort';
  const [lsSort, setLsSortState] = useState(() => {
    try { const v = JSON.parse(localStorage.getItem(LS_SORT_KEY) || 'null'); return v && v.col && ['asc', 'desc'].includes(v.dir) ? v : null; } catch (_) { return null; }
  });
  const setLsSort = (v) => { setLsSortState(v); try { if (v) localStorage.setItem(LS_SORT_KEY, JSON.stringify(v)); else localStorage.removeItem(LS_SORT_KEY); } catch (_) {} };

  // Same geometry as PickupQueue — the two tables sit side by side in the nav
  // and any drift between them reads as a bug. The column headers stick to the
  // toolbar's height, which here is measured rather than assumed.
  //
  // BLEED_L is larger than BLEED_R because S.main sets marginLeft:240 against a
  // 220px sidebar. Cancelling only the padding leaves the table flush right and
  // 20px short left, which looks like a mistake rather than a margin.
  // Measured, not hardcoded. This toolbar changes height with the view mode —
  // Risk View adds four filter pills, Manager Review adds a toggle and possibly
  // a Start Review button — and a fixed offset meant the column headers stuck
  // partway INSIDE the toolbar as soon as it grew. Measuring covers every mode
  // and any control added later.
  const toolbarRef = useRef(null);
  const [toolbarH, setToolbarH] = useState(46);
  useEffect(() => {
    const el = toolbarRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setToolbarH(el.offsetHeight));
    ro.observe(el);
    setToolbarH(el.offsetHeight);
    return () => ro.disconnect();
  }, []);

  // One number for every horizontal edge on this screen.
  //
  // S.main now sets marginLeft to the sidebar width and padding to the tables'
  // cell padding, so cancelling that padding is all it takes to run the toolbar
  // and table to the window edges — and the toolbar re-adds the same value as
  // inner padding, which lands its contents on the first column. Left gutter,
  // right gutter, and column inset are therefore identical by construction
  // rather than by three constants happening to agree.
  const PAGE_PAD = 14;
  const BLEED_L = PAGE_PAD;
  const BLEED_R = PAGE_PAD;
  const TABLE_PAD = PAGE_PAD;
  const ellipsis = { whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' };

  useEffect(() => {
    fetch(`${API}/api/settings`, { credentials:'include', headers: authHeaders() })
      .then(r => r.json())
      .then(data => setShopProfile(data?.shopProfile || {}))
      .catch(() => {});
  }, []);

  // Dismiss note popover when clicking outside
  useEffect(() => {
    if (!hoveredNote) return;
    const handler = () => setHoveredNote(null);
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, [hoveredNote]);

  const [payments, setPayments] = useState({});

  // silent: refresh the data without dropping the list into its loading state.
  // Used when the queue is already on screen and correct enough to look at —
  // rows update in place instead of the whole view flashing empty.
  // fresh: ask the server to skip its 30-second response cache. Only the
  // Refresh button sets it — see the note in server/routes/pickup.js.
  const loadTickets = useCallback(async (silent = false, fresh = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetch(`${API}/api/tickets${fresh ? '?fresh=1' : ''}`, { credentials:'include', headers: authHeaders() });
      if (!res.ok) throw new Error('API error');
      const data = await res.json();
      setTickets(data.tickets); setSummary(data.summary); setLoadError(false); setLastSync(new Date());
      setStale(!!data.degraded); setStaleAt(data.asOf || null); setBuilding(!!data.building);

      // Payment badges. Never fatal — a queue without badges beats no queue.
      const ids = (data.tickets || []).map(t => t.id).filter(Boolean);
      if (ids.length) {
        fetch(`${API}/api/payments/summary?workorderIds=${ids.join(',')}`, {
          credentials: 'include',
          headers: { Authorization: `Bearer ${localStorage.getItem('sb_token')}` },
        })
          .then(r => r.ok ? r.json() : { summary: {} })
          .then(d => setPayments(d.summary || {}))
          .catch(() => {});
      }
      // Load completion records — the definitive source of "checklist was submitted."
      // Single bulk call covers all tickets (not just review ones — the previous .filter
      // was a leftover bug that hid the badge on non-review tickets).
      try {
        const completedRes = await fetch(`${API}/api/completed-checklists`, { credentials:'include', headers: authHeaders() });
        const completedMap = await completedRes.json();
        setCompletedChecklists(completedMap || {});
      } catch (_) {
        setCompletedChecklists({});
      }
    } catch {
      // Deliberately NOT falling back to sample data. A failed load used to
      // fill the queue with fabricated tickets, which is what staff saw during
      // the Aug 14 502s. Keep whatever was last loaded, say so plainly, and
      // leave lastSync alone — it records the last real sync, not this attempt.
      setLoadError(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  const techMap = {};
  tickets.forEach(t => { techMap[t.tech] = (techMap[t.tech] || 0) + 1; });
  const techEntries = Object.entries(techMap).sort((a, b) => b[1] - a[1]);
  const maxTech = Math.max(...techEntries.map(e => e[1]), 1);
  const [completedChecklists, setCompletedChecklists] = useState({});
  const [changingStatus, setChangingStatus] = useState(null);
  const [statusChangeValue, setStatusChangeValue] = useState('');
  const [allStatuses, setAllStatuses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [changingTech, setChangingTech] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/workorder-statuses`, { credentials:'include', headers: authHeaders() })
      .then(r => r.json())
      .then(data => setAllStatuses(Array.isArray(data) ? data : []))
      .catch(() => {});
    fetch(`${API}/api/tickets/employees/list`, { credentials:'include', headers: authHeaders() })
      .then(r => r.json())
      .then(data => setEmployees(Array.isArray(data) ? data : []))
      .catch(() => {});
    fetch(`${API}/api/tickets/status-meta`, { credentials:'include', headers: authHeaders() })
      .then(r => r.json())
      .then(data => setStatusMeta(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const handleStatusChange = async (ticketId, statusId) => {
    if (!statusId) return;
    const status = allStatuses.find(s => s.id === statusId);
    if (!status) return;
    const prev = tickets.find(t => t.id === ticketId);
    // Optimistic update — instant UI change
    setTickets(ts => ts.map(t => t.id === ticketId ? { ...t, status: status.name } : t));
    setChangingStatus(null);
    try {
      await fetch(`${API}/api/tickets/${ticketId}/status`, {
        method:'PUT', credentials:'include',
        headers:{'Content-Type':'application/json', Authorization:`Bearer ${localStorage.getItem('sb_token')}`},
        body: JSON.stringify({ workorderStatusID: statusId, clearCompletion: true }),
      });
    } catch (err) {
      // Roll back on failure
      setTickets(ts => ts.map(t => t.id === ticketId ? { ...t, status: prev.status } : t));
      console.error('Status change failed:', err);
    }
  };

  const handleTechChange = async (ticketId, employeeId) => {
    if (!employeeId) return;
    const emp = employees.find(e => e.id === employeeId);
    if (!emp) return;
    const prev = tickets.find(t => t.id === ticketId);
    // Optimistic update — instant UI change
    setTickets(ts => ts.map(t => t.id === ticketId ? { ...t, tech: emp.name } : t));
    setChangingTech(null);
    try {
      await fetch(`${API}/api/tickets/${ticketId}/tech`, {
        method:'PUT', credentials:'include',
        headers: authHeaders(true),
        body: JSON.stringify({ employeeID: employeeId }),
      });
    } catch (err) {
      // Roll back on failure
      setTickets(ts => ts.map(t => t.id === ticketId ? { ...t, tech: prev.tech } : t));
      console.error('Tech reassign failed:', err);
    }
  };

  const openT = tickets.filter(t => !['Invoiced','Canceled','Done & Paid','Finished','Finished/Paid'].includes(t.status));
  const reviewTickets = tickets.filter(t => t.needsReview);
  const maxAge = Math.max(...AGE_BUCKETS.map(b => openT.filter(t => t.daysOpen >= b.min && t.daysOpen <= b.max).length), 1);

  // Names read A to Z first; numbers (days, flags, risk) biggest first.
  const FIRST_DIR = { ticket:'desc', customer:'asc', bike:'asc', tech:'asc', status:'asc', promise:'asc', days:'desc', flags:'desc', risk:'desc' };
  const handleSort = (col) => {
    if (viewMode === 'lightspeed') {
      const first = FIRST_DIR[col] || 'desc';
      if (!lsSort || lsSort.col !== col) setLsSort({ col, dir: first });
      else if (lsSort.dir === first) setLsSort({ col, dir: first === 'asc' ? 'desc' : 'asc' });
      else setLsSort(null);
      return;
    }
    if (sortCol === col) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortCol(col); setSortDir('desc'); }
  };

  // Lightspeed view — lookup maps derived from status metadata
  const statusSortMap = Object.fromEntries(statusMeta.map(s => [s.name.toLowerCase(), s.sortOrder]));
  const statusColorMap = Object.fromEntries(statusMeta.map(s => [s.name.toLowerCase(), s.htmlColor]));

  const hexToRgba = (hex, alpha) => {
    const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!r) return `rgba(128,128,128,${alpha})`;
    return `rgba(${parseInt(r[1],16)},${parseInt(r[2],16)},${parseInt(r[3],16)},${alpha})`;
  };

  const getLsStatusPillStyle = (statusName) => {
    const color = statusColorMap[statusName.toLowerCase()] || '#888888';
    return { ...S.statusPill(statusName), background: color, color: '#ffffff' };
  };

  const sortFns = {
    ticket: (a, b) => parseInt(a.id) - parseInt(b.id),
    customer: (a, b) => a.customer.localeCompare(b.customer),
    bike: (a, b) => a.bike.localeCompare(b.bike),
    tech: (a, b) => a.tech.localeCompare(b.tech),
    status: (a, b) => a.status.localeCompare(b.status),
    days: (a, b) => a.daysOpen - b.daysOpen,
    flags: (a, b) => a.flags.length - b.flags.length,
    risk: (a, b) => a.riskScore - b.riskScore,
    // No promise date sorts after any date, whichever way round.
    promise: (a, b) => (a.etaOut ? new Date(a.etaOut).getTime() : Infinity) - (b.etaOut ? new Date(b.etaOut).getTime() : Infinity),
  };
  // Lightspeed's own order: its status order, then oldest in first.
  const lsOrder = (a, b) => {
    const orderA = statusSortMap[a.status.toLowerCase()] ?? 99999;
    const orderB = statusSortMap[b.status.toLowerCase()] ?? 99999;
    if (orderA !== orderB) return orderA - orderB;
    return new Date(a.timeIn) - new Date(b.timeIn);
  };

  const filtered = (() => {
    let list = filter === 'all' ? tickets : tickets.filter(t => t.riskLevel === filter);
    if (managerReview) list = list.filter(t => t.needsReview);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(t =>
        t.customer.toLowerCase().includes(q) ||
        t.bike.toLowerCase().includes(q) ||
        t.displayId.toLowerCase().includes(q) ||
        t.tech.toLowerCase().includes(q) ||
        t.status.toLowerCase().includes(q)
      );
    }
    return list;
  })();

  const sorted = (() => {
    const list = [...filtered];
    if (viewMode === 'lightspeed') {
      if (!lsSort) return list.sort(lsOrder);
      // Status sorts in Lightspeed's status order, not alphabetically.
      const fn = lsSort.col === 'status' ? (a, b) => (statusSortMap[a.status.toLowerCase()] ?? 99999) - (statusSortMap[b.status.toLowerCase()] ?? 99999)
        : (sortFns[lsSort.col] || lsOrder);
      const dir = lsSort.dir === 'asc' ? 1 : -1;
      // Promise: undated tickets stay at the bottom both ways.
      if (lsSort.col === 'promise') {
        return list.sort((a, b) => (!a.etaOut - !b.etaOut) || dir * fn(a, b) || lsOrder(a, b));
      }
      return list.sort((a, b) => dir * fn(a, b) || lsOrder(a, b));
    }
    const fn = sortFns[sortCol] || sortFns.risk;
    return list.sort((a, b) => sortDir === 'asc' ? fn(a, b) : fn(b, a));
  })();

  // Everyone's tech time per ticket, shown under the tech's name.
  const timeTotals = useTimeTotals(tickets.map(t => t.id));

  const selectedIndex = sorted.findIndex(t => t.id === selectedTicketId);
  const goNext = () => { if (selectedIndex < sorted.length - 1) setSelectedTicketId(sorted[selectedIndex + 1].id); };
  const goPrev = () => { if (selectedIndex > 0) setSelectedTicketId(sorted[selectedIndex - 1].id); };

  return (
    <>
      {showNewTicket && (
        <NewTicketModal
          theme={theme}
          allStatuses={allStatuses}
          currentUser={currentUser}
          onClose={() => setShowNewTicket(false)}
          onCreated={(data) => {
            loadTickets();
            setSelectedTicketId(data.id);
          }}
        />
      )}
      {showWizard && (
        <ManagerReviewWizard
          tickets={reviewTickets}
          allStatuses={allStatuses}
          theme={theme}
          shopProfile={shopProfile}
          currentUser={currentUser}
          onClose={() => { setShowWizard(false); loadTickets(); }}
          onTicketUpdated={(ticketId, changes) => {
            setTickets(ts => ts.map(t => t.id === ticketId ? { ...t, ...changes } : t));
          }}
        />
      )}
      {selectedTicketId && <TicketDetail
        ticketId={selectedTicketId}
        ticket={sorted[selectedIndex] || null}
        ticketFlags={sorted[selectedIndex]?.flags || []}
        onClose={() => { setSelectedTicketId(null); loadTickets(true); }}
        onTicketUpdated={(id, changes) => setTickets(ts => ts.map(t => t.id === id ? { ...t, ...changes } : t))}
        onRemoved={(id) => setTickets(ts => ts.filter(t => String(t.id) !== String(id)))}
        onNext={goNext}
        onPrev={goPrev}
        currentIndex={selectedIndex}
        totalCount={sorted.length}
        theme={theme}
        currentUser={currentUser}
      />}

      {/* Customer popover */}
      {customerPopover && (
        <CustomerPopover
          customerId={customerPopover.customerId}
          customerName={customerPopover.customerName}
          anchorRef={{ current: customerAnchorRefs.current[customerPopover.customerId] }}
          onClose={() => setCustomerPopover(null)}
          onViewHistory={(summary) => {
            sessionStorage.setItem('spinboard-customer-prefill', JSON.stringify({ id: summary.id, name: summary.name }));
            window.dispatchEvent(new CustomEvent('spinboard-nav', { detail: { tab: 'customers', customerId: summary.id, customerName: summary.name } }));
          }}
          theme={theme}
        />
      )}

      {/* Receipt note popover — click to open, click outside to dismiss */}
      {hoveredNote && (() => {
        const popW = 300;
        const popH = 240;
        const spaceRight = window.innerWidth - hoveredNote.x;
        const spaceBelow = window.innerHeight - hoveredNote.y;
        const left = spaceRight < popW + 20
          ? Math.max(8, hoveredNote.x - popW - 12)  // flip left
          : hoveredNote.x + 12;
        const top = spaceBelow < popH
          ? Math.max(8, hoveredNote.y - popH + 20)  // flip up
          : hoveredNote.y - 8;
        return (
          <div
            onClick={e => e.stopPropagation()}
            style={{
              position:'fixed',
              left, top,
              zIndex:9999,
              width:popW,
              background: theme==='dark' ? '#1e2a40' : '#ffffff',
              border:`1px solid ${theme==='dark' ? '#2e3d58' : '#d0d0c8'}`,
              borderRadius:8,
              padding:'12px 14px',
              boxShadow:'0 8px 24px rgba(0,0,0,0.35)',
            }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
              <div style={{ fontSize:10, color: theme==='dark' ? '#5a6a8a' : '#999', textTransform:'uppercase', letterSpacing:'0.08em' }}>Receipt Note</div>
              <button onClick={() => setHoveredNote(null)} style={{ background:'transparent', border:'none', color: theme==='dark' ? '#5a6a8a' : '#aaa', cursor:'pointer', fontSize:16, lineHeight:1, padding:0 }}>×</button>
            </div>
            <div style={{ fontSize:12, color: theme==='dark' ? '#ccd0dc' : '#333', lineHeight:1.6, whiteSpace:'pre-wrap', wordBreak:'break-word', maxHeight:200, overflowY:'auto' }}>
              {hoveredNote.note}
            </div>
          </div>
        );
      })()}

      <div>
            <div style={{ ...S.topbar, background:T.bg }}>
              <div style={{ ...S.pageTitle, color:T.text }}>Service Queue</div>
              <div style={S.syncInfo}>
                <div style={S.syncDot(!loadError)} />
                {loadError ? (
                    <span style={{ color:'#e74c3c', fontWeight:600 }}>
                      Connection error{lastSync ? ` — showing data from ${fmtTime(lastSync)}` : ''}
                    </span>
                  ) : <span style={{ color:T.textSub }}>Live · {fmtTime(lastSync)}</span>}
                {/* + New Ticket moved to the sticky toolbar below. Live and
                    Refresh stay here and scroll away: you do not need them
                    mid-scroll, but a customer walking in while you are twenty
                    rows down is exactly when you want New Ticket. */}
                <button style={{ ...S.btn(false), padding:'5px 14px', marginLeft:8, color:T.textSub, borderColor:T.filterBorder }} onClick={() => loadTickets(false, true)}>↻ Refresh</button>
              </div>
            </div>

            {(stale || building) && (
              <div style={{ fontSize:12, color:T.textSub, marginBottom:12 }}>
                {!staleAt ? 'Updating\u2026' : `Updated ${staleAgeText()}`}
              </div>
            )}

            {summary && (
              <div style={S.kpiGrid}>
                <div style={{ ...S.kpi(), background:T.kpiCard, border:`1px solid ${T.cardBorder}` }}><div style={{ ...S.kpiLabel, color:T.textSub }}>Open Tickets</div><div style={{ ...S.kpiValue(), color:T.kpiText }}>{summary.openCount}</div><div style={{ ...S.kpiSub, color:T.textSub }}>Active in queue</div></div>
                <div style={{ ...S.kpi('#e74c3c'), background:T.kpiCard, border:`1px solid ${T.cardBorder}`, borderTop:'3px solid #e74c3c' }}><div style={{ ...S.kpiLabel, color:T.textSub }}>High Risk</div><div style={S.kpiValue('#e74c3c')}>{summary.high}</div><div style={{ ...S.kpiSub, color:T.textSub }}>Need immediate action</div></div>
                <div style={{ ...S.kpi('#e67e22'), background:T.kpiCard, border:`1px solid ${T.cardBorder}`, borderTop:'3px solid #e67e22' }}><div style={{ ...S.kpiLabel, color:T.textSub }}>Medium Risk</div><div style={S.kpiValue('#e67e22')}>{summary.medium}</div><div style={{ ...S.kpiSub, color:T.textSub }}>Monitor closely</div></div>
                <div style={{ ...S.kpi('#f1c40f'), background:T.kpiCard, border:`1px solid ${T.cardBorder}`, borderTop:'3px solid #f1c40f' }}><div style={{ ...S.kpiLabel, color:T.textSub }}>High Value</div><div style={S.kpiValue('#f1c40f')}>{summary.highValue}</div><div style={{ ...S.kpiSub, color:T.textSub }}>{summary.totalValue} labor & misc</div></div>
              </div>
            )}

            <div style={S.chartGrid}>
              <div style={{ ...S.chartCard, background:T.chartCard, border:`1px solid ${T.cardBorder}` }}>
                <div style={{ ...S.chartTitle, color:T.textSub }}>Tickets by technician</div>
                {techEntries.map(([name, count], i) => (
                  <div key={name} style={S.barRow}>
                    <span style={{ ...S.barLabel, color:T.tableHead }}>{name}</span>
                    <div style={{ ...S.barTrack, background:T.barTrack }}><div style={S.barFill(TECH_COLORS[i % TECH_COLORS.length], (count/maxTech)*100)} /></div>
                    <span style={{ ...S.barCount, color:T.textSub }}>{count}</span>
                  </div>
                ))}
              </div>
              <div style={{ ...S.chartCard, background:T.chartCard, border:`1px solid ${T.cardBorder}` }}>
                <div style={{ ...S.chartTitle, color:T.textSub }}>Queue age distribution</div>
                {AGE_BUCKETS.map(b => {
                  const count = openT.filter(t => t.daysOpen >= b.min && t.daysOpen <= b.max).length;
                  return (
                    <div key={b.label} style={S.barRow}>
                      <span style={{ ...S.barLabel, color:T.tableHead }}>{b.label}</span>
                      <div style={{ ...S.barTrack, background:T.barTrack }}><div style={S.barFill(b.color, (count/maxAge)*100)} /></div>
                      <span style={{ ...S.barCount, color:T.textSub }}>{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ ...S.section }}>
              {/* Toolbar and table both bleed past the page padding and the
                  sidebar gutter — ten columns want every pixel. The toolbar has
                  to bleed too, not just the table: it is the sticky element, and
                  a background narrower than the rows beneath would let them
                  scroll past visible at the edges. */}
              {/* Two deliberate rows rather than one that wraps — the control set
                  changes size with the view mode, and a single wrapping row left
                  the search box stranded on its own line looking like a mistake.
                  Row 1 is the action and the count; row 2 pairs search with the
                  view settings, which is where they read best: find on the left,
                  change what you are looking at on the right. */}
              <div ref={toolbarRef} style={{
                position:'sticky', top:0, zIndex:20, background:T.bg,
                display:'flex', flexDirection:'column', gap:10,
                paddingTop:2, paddingBottom:10,
                marginLeft:-BLEED_L, marginRight:-BLEED_R, paddingLeft:TABLE_PAD, paddingRight:TABLE_PAD,
              }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:16 }}>
                  {/* Slimmer than a topbar button: it has a row to itself now, so
                      it does not need weight to be found. */}
                  <div style={{ display:'flex', gap:8 }}>
                    <button style={{ ...S.btn(true), background:'#e67e22', color:'#fff', padding:'5px 14px', fontSize:12, whiteSpace:'nowrap' }}
                      onClick={() => setShowNewTicket(true)}>+ New Ticket</button>
                    {/* Quotes (23 Oct): where staff are when a customer asks "how much would it be". */}
                    {currentUser?.permissions?.quotes !== false && (
                      <button style={{ ...S.btn(false), color:T.text, borderColor:T.border, padding:'4px 14px', fontSize:12, whiteSpace:'nowrap' }}
                        onClick={() => window.dispatchEvent(new CustomEvent('spinboard-nav', { detail: { tab: 'quotes', newQuote: true } }))}>+ New Quote</button>
                    )}
                  </div>
                  <span style={{ ...S.sectionTitle, color:T.tableHead, whiteSpace:'nowrap' }}>
                    Service tickets <CountPill n={sorted.length} T={T} style={{ marginLeft: 4 }} />{search ? <span style={{ marginLeft: 6 }}>of {tickets.length}</span> : null}
                  </span>
                </div>

                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:16, flexWrap:'wrap' }}>
                  <div style={{ position:'relative', display:'flex', alignItems:'center' }}>
                    <span style={{ position:'absolute', left:12, color:T.textSub, fontSize:15, pointerEvents:'none' }}>⌕</span>
                    <input type="text" placeholder="Search customer, bike, ticket…" value={search}
                      onChange={e => { setSearch(e.target.value); setSelectedTicketId(null); }}
                      style={{ background:T.card, border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'9px 30px 9px 34px', color:T.text, fontSize:13.5, fontFamily:"'Inter', sans-serif", width:320, outline:'none' }}
                    />
                    {search && <button onClick={() => setSearch('')} style={{ position:'absolute', right:9, background:'transparent', border:'none', color:T.textSub, cursor:'pointer', fontSize:16, lineHeight:1 }}>×</button>}
                  </div>
                  <div style={{ ...S.filterRow, flexWrap:'wrap', justifyContent:'flex-end' }}>
                    {viewMode !== 'lightspeed' && [
                      { k:'all',    l:'All' },
                      { k:'high',   l:'High risk' },
                      { k:'medium', l:'Medium risk' },
                      { k:'none',   l:'Clean' },
                    ].map(f => (
                      <button key={f.k} style={{ ...S.filterBtn(filter===f.k),
                        background: filter===f.k ? T.filterActive : T.filterInactive,
                        color: filter===f.k ? T.filterActiveText : T.filterInactiveText,
                        border: filter===f.k ? 'none' : `1px solid ${T.filterBorder}`,
                      }} onClick={() => setFilter(f.k)}>{f.l}</button>
                    ))}
                    {viewMode === 'lightspeed' && lsSort && (
                      <button onClick={() => setLsSort(null)} title="Back to the order Lightspeed shows"
                        style={{ ...S.filterBtn(false), background:T.filterInactive, color:T.filterInactiveText, border:`1px solid ${T.filterBorder}` }}>
                        ↺ Lightspeed order
                      </button>
                    )}
                    {/* View mode toggle — Spinboard vs Lightspeed */}
                    <div style={{ display:'flex', borderRadius:6, overflow:'hidden', border:`1px solid ${T.filterBorder}` }}>
                      {[{k:'lightspeed',l:'Lightspeed View'},{k:'spinboard',l:'Risk View'}].map(({k,l}) => (
                        <button key={k} onClick={() => { setViewMode(k); if (k === 'lightspeed') setFilter('all'); }}
                          style={{ padding:'5px 14px', border:'none', cursor:'pointer', fontSize:12, fontWeight: viewMode===k ? 600 : 400,
                            background: viewMode===k
                              ? (k==='spinboard' ? (isDark ? '#2a1f12' : '#fff0e8') : (isDark ? '#12243a' : '#dbeafe'))
                              : 'transparent',
                            color: viewMode===k
                              ? (k==='spinboard' ? '#F4622A' : (isDark ? '#60a5fa' : '#1d4ed8'))
                              : T.textSub,
                            transition:'all 0.15s', fontFamily:"'Inter', sans-serif",
                            borderRight: k==='lightspeed' ? `1px solid ${T.filterBorder}` : 'none',
                          }}>
                          {l}
                        </button>
                      ))}
                    </div>
                    {/* Manager Review toggle – owner/manager only */}
                    {['owner','manager'].includes(currentUser?.role) && <div
                      onClick={() => setManagerReview(v => !v)}
                      style={{ display:'flex', alignItems:'center', gap:7, padding:'5px 12px', borderRadius:6, cursor:'pointer', userSelect:'none',
                        background: managerReview ? '#9b59b622' : 'transparent',
                        border: `1px solid ${managerReview ? '#9b59b6' : T.filterBorder}`,
                        transition:'all 0.15s',
                      }}>
                      <div style={{ width:28, height:16, borderRadius:8, background: managerReview ? '#9b59b6' : (isDark ? '#2e3d58' : '#d0d0c8'), position:'relative', transition:'all 0.2s', flexShrink:0 }}>
                        <div style={{ width:12, height:12, borderRadius:'50%', background:'white', position:'absolute', top:2, left: managerReview ? 14 : 2, transition:'all 0.2s' }} />
                      </div>
                      <span style={{ fontSize:12, color: managerReview ? '#9b59b6' : T.textSub, fontWeight: managerReview ? 600 : 400 }}>Manager Review</span>
                    </div>}
                    {/* Start Review Wizard */}
                    {['owner','manager'].includes(currentUser?.role) && managerReview && reviewTickets.length > 0 && (
                      <button onClick={() => setShowWizard(true)}
                        style={{ ...S.btn(true), background:'#9b59b6', color:'#fff', fontSize:12, padding:'5px 14px',
                          display:'flex', alignItems:'center', gap:6 }}>
                        ▶ Start Review ({reviewTickets.length})
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {loading ? <div style={{ ...S.emptyRow, color:T.emptyRow }}>Loading tickets…</div> : (
                <div style={{ marginLeft:-BLEED_L, marginRight:-BLEED_R }}>
                <table style={{ ...S.table, tableLayout:'fixed' }}>
                  <thead>
                    <tr>
                      {[
                        { key:'ticket',   label:'Ticket #', w:'8%'  },
                        { key:'customer', label:'Customer', w:'14%' },
                        // Truncates, same as Ready for Pickup. "Trek Blue"
                        // identifies the bike; the full catalog string does not
                        // need to, and Tech/Status/Flags have no width to spare.
                        { key:'bike',     label:'Bike',     w:'13%', clip:true },
                        { key:'tech',     label:'Tech',     w:'11%' },
                        { key:'status',   label:'Status',   w:'10%' },
                        { key:'promise',  label:'Promise',  w:'9%'  },
                        { key:'days',     label:'Days',     w:'6%'  },
                        { key:null,       label:'Alerts',   w:'8%'  },
                        { key:'flags',    label:'Flags',    w:'13%' },
                        { key:'risk',     label:'Risk',     w:'8%'  },
                      ].map((col, i) => (
                        (() => {
                          // Lightspeed view sorts too (7 Oct); with no header
                          // chosen it keeps Lightspeed's own order.
                          const on = col.key && (viewMode === 'lightspeed' ? lsSort?.col === col.key : sortCol === col.key);
                          const dir = viewMode === 'lightspeed' ? lsSort?.dir : sortDir;
                          return (
                        <th key={i} width={col.w} style={{ ...S.th, width:col.w, color: on ? '#F4622A' : T.tableHead, background:T.card, position:'sticky', top:toolbarH, zIndex:10, borderBottom:`1px solid ${T.border}`, cursor: col.key ? 'pointer' : 'default', userSelect:'none', whiteSpace:'nowrap' }}
                          title={col.key && viewMode === 'lightspeed' ? (on ? 'Click again to reverse; a third click goes back to Lightspeed order' : `Sort by ${col.label}`) : undefined}
                          onClick={() => col.key && handleSort(col.key)}>
                          {col.label}
                          {col.key && <span style={{ marginLeft:4, color: on ? '#F4622A' : T.inputBorder }}>
                            {on ? (dir==='asc' ? '↑' : '↓') : '↕'}
                          </span>}
                        </th>
                          );
                        })()
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.length===0
                      ? <tr><td colSpan={10} style={{ ...S.emptyRow, color:T.emptyRow }}>{search ? `No tickets matching "${search}"` : 'No tickets in this category ✓'}</td></tr>
                      : sorted.map(t => {
                          const isActive = t.id === selectedTicketId;
                          const isDark = theme === 'dark';
                          // Actionable flags only — exclude high value and no receipt note from row
                          const rowFlags = t.flags.filter(f => f.level !== 'revenue' && f.label !== 'No receipt note');
                          const highValueFlag = t.flags.find(f => f.level === 'revenue');
                          const noReceiptNote = !t.hasReceiptNote;
                          const hasCompletedChecklist = !!completedChecklists[t.id];

                          return (
                            <tr key={t.id}
                              onClick={() => {
                                setSelectedTicketId(isActive ? null : t.id);
                                // Opening it is what clears the tag (server side too).
                                if (t.quoteApproved) setTickets(ts => ts.map(x => x.id === t.id ? { ...x, quoteApproved: null } : x));
                              }}
                              style={{ cursor: 'pointer',
                                background: isActive
                                  ? (theme==='light' ? '#e8f5e9' : '#1e2a1e')
                                  : viewMode==='lightspeed'
                                    ? hexToRgba(statusColorMap[t.status.toLowerCase()] || '#888888', 0.15)
                                    : 'transparent',
                                borderLeft: isActive ? '2px solid #2ecc71' : '2px solid transparent' }}
                              onMouseEnter={e => {
                                if (!isActive) e.currentTarget.style.background = viewMode==='lightspeed'
                                  ? hexToRgba(statusColorMap[t.status.toLowerCase()] || '#888888', 0.28)
                                  : (theme==='light' ? '#f4f4ef' : '#1e2a40');
                              }}
                              onMouseLeave={e => {
                                if (!isActive) e.currentTarget.style.background = viewMode==='lightspeed'
                                  ? hexToRgba(statusColorMap[t.status.toLowerCase()] || '#888888', 0.15)
                                  : 'transparent';
                              }}
                            >
                              {/* Ticket # */}
                              <td style={{...S.td, color:T.textSub, borderBottom:`1px solid ${T.border}`, ...S.mono}}>
                                {t.displayId}
                                {/* Quotes (24 Oct): the customer approved a quote on
                                    this ticket and nobody has opened it since. */}
                                {t.quoteApproved && (
                                  <div title={`${t.quoteApproved} approved. Open the ticket to clear this.`}
                                    style={{ marginTop:3, fontFamily:'inherit', fontSize:10, fontWeight:700, letterSpacing:'0.03em', whiteSpace:'nowrap',
                                      display:'inline-block', padding:'1px 6px', borderRadius:8, color:'#fff', background:'#27ae60' }}>
                                    {String(t.quoteApproved).startsWith('Estimate') ? 'ESTIMATE APPROVED' : 'QUOTE APPROVED'}
                                  </div>
                                )}
                                {/* A tech asked for an estimate (26 Oct); clears when one is sent. */}
                                {t.needsEstimate && (
                                  <div title="A tech asked for an estimate. Send it from the ticket, or from Quotes, Needs estimate."
                                    style={{ marginTop:3, fontFamily:'inherit', fontSize:10, fontWeight:700, letterSpacing:'0.03em', whiteSpace:'nowrap',
                                      display:'inline-block', padding:'1px 6px', borderRadius:8, color:'#fff', background:'#d35400' }}>
                                    NEEDS ESTIMATE
                                  </div>
                                )}
                              </td>
                              {/* Customer */}
                              <td style={{...S.td, color:T.text, fontWeight:500, borderBottom:`1px solid ${T.border}`}}>
                                <span
                                  ref={el => { if (el) customerAnchorRefs.current[t.customerId] = el; }}
                                  onClick={e => {
                                    e.stopPropagation();
                                    if (customerPopover?.customerId === t.customerId) {
                                      setCustomerPopover(null);
                                    } else {
                                      setCustomerPopover({ customerId: t.customerId, customerName: t.customer });
                                    }
                                  }}
                                  style={{ cursor:'pointer', borderBottom:`1px dashed ${isDark ? '#3a4a6a' : '#c0c0b8'}`, paddingBottom:1 }}
                                >{t.customer}</span>
                              </td>
                              {/* Bike */}
                              <td title={t.bike} style={{...S.td, ...ellipsis, color:T.textSub, fontSize:12, borderBottom:`1px solid ${T.border}`}}>{t.bike}</td>
                              {/* Tech — click to reassign */}
                              <td style={{...S.td, color:T.tableText, borderBottom:`1px solid ${T.border}`}}
                                onClick={e => e.stopPropagation()}>
                                {changingTech === t.id ? (
                                  <select autoFocus
                                    defaultValue=""
                                    onChange={e => { if (e.target.value) handleTechChange(t.id, e.target.value); }}
                                    onBlur={() => setChangingTech(null)}
                                    style={{ background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6,
                                      padding:'3px 8px', color:T.text, fontSize:12, outline:'none', cursor:'pointer',
                                      fontFamily:'Arial, sans-serif' }}>
                                    <option value="">{t.tech}</option>
                                    {employees.filter(e => e.name !== t.tech).map(e => (
                                      <option key={e.id} value={e.id}>{e.name}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <span
                                    title="Click to reassign"
                                    onClick={() => setChangingTech(t.id)}
                                    style={{ cursor:'pointer', borderBottom:`1px dashed ${isDark ? '#3a4a6a' : '#b0b0a8'}`,
                                      paddingBottom:1, fontSize:13 }}>
                                    {t.tech}
                                  </span>
                                )}
                                {timeTotals[String(t.id)] && changingTech !== t.id && (
                                  <div style={{ marginTop:4 }}><TimeBadge total={timeTotals[String(t.id)]} isDark={isDark} /></div>
                                )}
                              </td>
                              {/* Status — click to change */}
                              <td style={{...S.td, borderBottom:`1px solid ${T.border}`}}
                                onClick={e => e.stopPropagation()}>
                                {!['owner','manager'].includes(currentUser?.role) ? (
                                  <span style={{ cursor:'default' }}>
                                    <span style={viewMode==='lightspeed' ? getLsStatusPillStyle(t.status) : S.statusPill(t.status)}>{t.status}</span>
                                  </span>
                                ) : changingStatus === t.id ? (
                                  <select autoFocus
                                    defaultValue=""
                                    onChange={e => { if (e.target.value) handleStatusChange(t.id, e.target.value); }}
                                    onBlur={() => setChangingStatus(null)}
                                    style={{ background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6,
                                      padding:'3px 8px', color:T.text, fontSize:12, outline:'none', cursor:'pointer',
                                      fontFamily:'Arial, sans-serif' }}>
                                    {/* Current status, shown as the resting value. Coloured like the rest —
                                        an uncoloured row at the top reads as a different status, not as
                                        "where this ticket already is". */}
                                    {(() => {
                                      const cur = allStatuses.find(st => st.name === t.status);
                                      return (
                                        <option value=""
                                          style={cur?.color ? { background: cur.color, color: readableOn(cur.color) } : undefined}>{t.status}</option>
                                      );
                                    })()}
                                    {allStatuses.filter(s => {
                                      const n = s.name.toLowerCase();
                                      // Exclude the current status: it is already the resting option above,
                                      // and listing it again made every dropdown show the ticket's status
                                      // twice. Same rule the tech picker two columns over already uses.
                                      if (s.name === t.status) return false;
                                      return !(n.includes('done') && n.includes('paid')) && n !== 'invoiced';
                                    }).map(s => (
                                      <option key={s.id} value={s.id}
                                        style={s.color ? { background: s.color, color: readableOn(s.color) } : undefined}>{s.name}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <span
                                    title="Click to change status"
                                    onClick={() => setChangingStatus(t.id)}
                                    style={{ cursor:'pointer' }}>
                                    <span style={viewMode==='lightspeed' ? getLsStatusPillStyle(t.status) : S.statusPill(t.status)}>{t.status}</span>
                                  </span>
                                )}
                              </td>
                              {/* Promise date — right of Status */}
                              <td style={{...S.td, borderBottom:`1px solid ${T.border}`, whiteSpace:'nowrap'}}>
                                {t.etaOut ? (
                                  <span style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize:12, fontWeight:500,
                                    color: t.isOverdue ? '#e74c3c' : t.isDueSoon ? '#e67e22' : T.textSub }}>
                                    {new Date(t.etaOut).toLocaleDateString('en-US', { month:'short', day:'numeric' })}
                                  </span>
                                ) : <span style={{ color:T.textMuted }}>—</span>}
                              </td>
                              {/* Days */}
                              <td style={{...S.td, borderBottom:`1px solid ${T.border}`}}><span style={S.days(t.daysOpen)}>{t.daysOpen}d</span></td>
                              {/* Alerts — Tabler icons for high value, no receipt note, and receipt note preview */}
                              <td style={{...S.td, borderBottom:`1px solid ${T.border}`, textAlign:'center', width:60}}
                                onClick={e => e.stopPropagation()}>
                                <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:7 }}>
                                  {highValueFlag && (
                                    <i className="ti ti-moneybag"
                                      title={highValueFlag.label}
                                      aria-hidden="true"
                                      style={{ fontSize:18, color:'#2ecc71', cursor:'default', lineHeight:1 }}
                                    />
                                  )}
                                  {noReceiptNote ? (
                                    <i className="ti ti-note-off"
                                      title="No receipt note"
                                      aria-hidden="true"
                                      style={{ fontSize:18, color:'#e74c3c', cursor:'default', lineHeight:1 }}
                                    />
                                  ) : t.hasReceiptNote && (
                                    <i className="ti ti-notes"
                                      aria-hidden="true"
                                      style={{ fontSize:18, color: hoveredNote?.ticketId === t.id ? '#89b4e0' : '#5a8ab8', cursor:'pointer', lineHeight:1 }}
                                      onClick={e => {
                                        e.stopPropagation();
                                        if (hoveredNote?.ticketId === t.id) {
                                          setHoveredNote(null);
                                        } else {
                                          const rect = e.currentTarget.getBoundingClientRect();
                                          setHoveredNote({ ticketId: t.id, note: t.note || '(note unavailable)', x: rect.left, y: rect.top });
                                        }
                                      }}
                                    />
                                  )}
                                </div>
                              </td>
                              {/* Flags — actionable only */}
                              <td style={{...S.td, minWidth:200, borderBottom:`1px solid ${T.border}`}}>
                                <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                                  <PaymentBadge payment={payments[String(t.id)]} theme={theme} />
                                  {hasCompletedChecklist && (
                                    <span style={{ fontSize:11, padding:'3px 9px', borderRadius:4, fontWeight:500,
                                      background: theme === 'light' ? '#d4edda' : '#0f2a15',
                                      color: theme === 'light' ? '#1a7a3a' : '#2ecc71',
                                      whiteSpace:'nowrap', display:'inline-flex', alignItems:'center', gap:4 }}>
                                      ✓ Checklist done
                                    </span>
                                  )}
                                  {rowFlags.length === 0 && !hasCompletedChecklist
                                    ? <span style={{ ...S.badgeClean, background: theme==='light' ? '#d4edda' : '#0f2a15', color: theme==='light' ? '#1a7a3a' : '#2ecc71' }}>Clean</span>
                                    : rowFlags.map((f, i) => {
                                        const bc = f.level === 'danger'
                                          ? { bg: theme==='light' ? '#fde8e8' : '#2a0f0f', color:'#e74c3c' }
                                          : { bg: theme==='light' ? '#fef3e2' : '#2a1f0a', color:'#e67e22' };
                                        return <span key={i} style={{ fontSize:11, padding:'3px 9px', borderRadius:4, fontWeight:500, background:bc.bg, color:bc.color, whiteSpace:'nowrap', display:'inline-block' }}>{f.label}</span>;
                                      })
                                  }
                                </div>
                              </td>
                              {/* Risk bar */}
                              <td style={{...S.td, borderBottom:`1px solid ${T.border}`}}>
                                <div style={{ ...S.riskBar, background: theme==='light' ? '#e0e0d8' : '#243050' }}>
                                  <div style={S.riskFill(Math.min(t.riskScore, 100))} />
                                </div>
                              </td>
                            </tr>
                          );
                        })
                    }
                  </tbody>
                </table>
                </div>
              )}
              <div style={{ fontSize:11, color:T.textSub, marginTop:10, textAlign:'right' }}>Click any row to view details · ← → to navigate · Esc to close</div>
            </div>
      </div>
    </>
  );
}
